import { GridOptions, ValueFormatterParams } from "ag-grid-community";
import { ColDef } from "ag-grid-community/dist/lib/entities/colDef";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";
import { useImmer } from "use-immer";

import { IgTableWrapper, IgTypography } from "@intelligems/ig-theme";
import {
  AnalyzeResourceResponseBodyType,
  MetricArrayType,
  MetricType,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { UseQueryResult } from "@tanstack/react-query";

import { useExperimentAnalysis } from "../../../../api/analysis/analysis";
import { useConfiguration } from "../../../../api/configurations/use-configuration";
import { useGetExperiencesList } from "../../../../api/experiences-list/get-experiences-list";
import {
  currencyFormatter,
  noDecimalNumberFormatter,
  percentTwoDecimalFormatter,
} from "../../../../api/formatters/dashboard";
import {
  newChannelList,
  trafficSourceList,
} from "../../../../components/AudienceFilters/Channel";
import theme from "../../../../theme";
import { VariationFe } from "../../../../types/schemas/variations";
import {
  overviewTableRowGroupSort,
  sortByOrder,
} from "../../../../utils/sortings";
import { overviewTableCompareBaseConfig } from "../../Graphs/Utils/graphConfigs";
import { getIdOfLargestMetric } from "../../Utils/utils";

import { CompareInnerRenderer } from "./CompareInnerRenderer";
import { TableHeader } from "./TableHeader";

const PREFIX = "DashboardTable";

const classes = {
  root: `${PREFIX}-root`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.root}`]: {
    "& .MuiDataGrid-iconSeparator": {
      display: "none",
    },
    "& .MuiDataGrid-renderingZone": {
      "& .MuiDataGrid-row": {
        "&:last-child": {
          "& div": {
            borderBottom: "0px",
          },
        },
      },
    },
  },
  [`& .ag-theme-alpine .ag-cell`]: {
    lineHeight: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  "& ::-webkit-scrollbar": {
    width: 0 /* Remove scrollbar space */,
    background: "transparent" /* Optional: just make scrollbar invisible */,
  },
  [`& .ag-root-wrapper`]: {
    border: "solid 0px !important",
    borderRadius: "15px",
    fontFamily: `${theme.typography.fontFamily}`,
    fontSize: "14px",
    fontWeight: 400,
  },
  // row border bottom color
  [`& .ag-theme-alpine .ag-row`]: {
    borderBottom: "1px solid #ECECEC",
  },
  // header bottom border color
  [`& .ag-header`]: {
    borderBottom: "solid 1px #ECECEC !important",
  },
  [`& .ag-header-cell-label`]: {
    fontWeight: 400,
    fontSize: "14px",
  },
  [`& .ag-cell-wrap-text`]: {
    wordBreak: "break-word",
  },
  [`& .ag-header-cell`]: {
    backgroundColor: "white !important",
  },
  [`& .ag-header-cell-text`]: {
    color: `${theme.palette.secondary.main} !important`,
    fontWeight: 600,
    fontSize: "14px",
  },
  [`& .ag-cell`]: {
    fontSize: "13px !important",
    color: theme.palette.secondary.main,
  },
  [`& .ag-cell-wrapper.ag-row-group`]: {
    alignItems: "center !important",
  },
}));

const tabValueMap = {
  0: "All",
  1: "DEVICE_TYPE",
  2: "NEW_VS_RETURNING_VISITOR",
  3: "CHANNEL",
  4: "CHANNEL",
};

interface OverviewTableProps {
  data?: MetricArrayType;
  variations: VariationFe[];
}

export const OverviewTable = ({ data, variations }: OverviewTableProps) => {
  const experiencesList = useGetExperiencesList({ category: "experiment" });
  const shouldShowProfit = useConfiguration<boolean>(
    "should_show_profit_in_dashboard"
  );
  const [tabValue, setTabValue] = useImmer(0);

  const compareDeviceData: UseQueryResult<AnalyzeResourceResponseBodyType>[] =
    useExperimentAnalysis([
      Object.assign(overviewTableCompareBaseConfig[0], {
        ...overviewTableCompareBaseConfig[0],
        datasetId: "compareDeviceType",
      }),
    ]);

  const compareVisitorData: UseQueryResult<AnalyzeResourceResponseBodyType>[] =
    useExperimentAnalysis([
      Object.assign(overviewTableCompareBaseConfig[0], {
        ...overviewTableCompareBaseConfig[0],
        datasetId: "compareNewReturning",
      }),
    ]);

  const compareChannelData: UseQueryResult<AnalyzeResourceResponseBodyType>[] =
    useExperimentAnalysis([
      Object.assign(overviewTableCompareBaseConfig[0], {
        ...overviewTableCompareBaseConfig[0],
        datasetId: "compareChannel",
      }),
    ]);
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const gridRef = useRef<AgGridReact>(null);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 100,
      resizable: false,
      cellStyle: { fontSize: "12px" },
      menuTabs: [],
      suppressMovable: true,
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: "",
      minWidth: 200,
    };
  }, []);

  const groupRowRendererParams = {
    innerRenderer: CompareInnerRenderer,
    suppressCount: true,
  };

  const rows = useMemo(() => {
    if (tabValue === 0) {
      return (data ?? [])
        ?.map((group: MetricType) => {
          for (const testGroup of variations) {
            if (group.label === testGroup.id) {
              return { ...group, order: testGroup.order };
            }
          }
          return { ...group, order: 99 };
        })
        .sort(sortByOrder)
        ?.map((data, index) => {
          return {
            ...data,
            id: index,
          };
        });
    }
    if (tabValue === 1) {
      if (
        compareDeviceData &&
        compareDeviceData[0] &&
        compareDeviceData[0].data &&
        compareDeviceData[0].data.metrics
      ) {
        return overviewTableRowGroupSort(
          compareDeviceData[0].data!.metrics,
          "DEVICE_TYPE"
        )
          ?.map((group: MetricType) => {
            for (const testGroup of variations) {
              if (group.label === testGroup.id) {
                return { ...group, order: testGroup.order };
              }
            }
            return { ...group, order: 99 };
          })
          .sort(sortByOrder)
          .map((metric, index) => {
            return {
              ...metric,
              id: index,
            };
          });
      }
    }

    if (tabValue === 2) {
      if (
        compareVisitorData &&
        compareVisitorData[0] &&
        compareVisitorData[0].data &&
        compareVisitorData[0].data.metrics
      ) {
        return overviewTableRowGroupSort(
          compareVisitorData[0].data!.metrics,
          "NEW_VS_RETURNING_VISITOR"
        )
          ?.map((group: MetricType) => {
            for (const testGroup of variations) {
              if (group.label === testGroup.id) {
                return { ...group, order: testGroup.order };
              }
            }
            return { ...group, order: 99 };
          })
          .sort(sortByOrder)
          .map((metric, index) => {
            return {
              ...metric,
              id: index,
            };
          });
      }
    }

    if (tabValue === 4 || tabValue === 3) {
      if (
        compareChannelData &&
        compareChannelData[0] &&
        compareChannelData[0].data &&
        compareChannelData[0].data.metrics
      ) {
        const filteredAndSorted = compareChannelData[0]
          .data!.metrics?.filter((metric) =>
            (tabValue === 4
              ? [...newChannelList, { key: "Other Channel", label: "Other" }]
              : [
                  ...trafficSourceList,
                  { key: "Other Traffic Source", label: "Other" },
                ]
            )
              .map((list) => list.key)
              .includes(metric["CHANNEL"])
          )
          ?.map((group: MetricType) => {
            for (const testGroup of variations) {
              if (group.label === testGroup.id) {
                return { ...group, order: testGroup.order };
              }
            }
            return { ...group, order: 99 };
          })
          .sort(sortByOrder)
          .map((metric, index) => {
            return {
              ...metric,
              id: index,
            };
          });
        return overviewTableRowGroupSort(filteredAndSorted, "CHANNEL");
      }
    }
    return [];
  }, [
    compareVisitorData,
    compareChannelData,
    compareDeviceData,
    data,
    tabValue,
  ]);

  const loadingCellRendererParams = useMemo(() => {
    return {
      loadingMessage: "One moment please...",
    };
  }, []);

  const gridOptions = useMemo<GridOptions>(() => {
    return {
      rowData: data,
    };
  }, []);

  const getIdOfLargestColumnValue = useCallback(
    (columnName: string, filterKey: string, filterValue: string) => {
      if (!rows) {
        return null;
      }

      return getIdOfLargestMetric(columnName, filterKey, filterValue, rows);
    },
    [rows]
  );

  const collapse = useMemo(() => {
    return -1;
    // comment back in when we want it auto collapsed
    // if (tabValue === 3) {
    //   return 0;
    // } else return -1;
  }, [tabValue]);

  const columnDefs: ColDef[] = useMemo(() => {
    const currValue = tabValueMap[tabValue];
    const profit = {
      headerName: "Profit per Visitor",
      headerClass: "header-custom",
      flex: 0.4,
      field: "PROFIT_PER_VISITOR",
      cellStyle: {
        justifyContent: "flex-start",
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return currencyFormatter(
          params.value as number,
          false,
          storeCurrencyConfig!.value!
        );
      },
      cellRenderer: (params) => {
        const isHighestValue =
          getIdOfLargestColumnValue(
            "PROFIT_PER_VISITOR",
            currValue,
            params?.data[currValue]
          ) === params?.data?.id;
        return (
          <IgTypography
            sx={{
              color: isHighestValue
                ? theme.palette.success.main
                : theme.palette.secondary.main,
              fontSize: "13px",
              fontStyle: params.value === "All Groups" ? "italic" : "none",
            }}
          >
            {params.valueFormatted}
          </IgTypography>
        );
      },
    };
    const baseColumns = [
      {
        headerName: "",
        flex: 0.4,
        field: "",
      },
      {
        headerName: "none",
        rowGroup: true,
        hide: true,
        field: currValue,
        suppressFillHandle: true,
      },
      {
        headerName: "Group",
        headerClass: "header-custom",
        field: "label",
        flex: 0.4,
        cellStyle: {
          justifyContent: "flex-start",
        },
        cellRenderer: (params) => {
          return (
            <IgTypography
              sx={{
                color: theme.palette.secondary.main,
                fontSize: "13px",
                fontStyle: params.value === "All Groups" ? "italic" : "none",
              }}
            >
              {variations.find((v) => v.id === params.value)?.name || ""}
            </IgTypography>
          );
        },
      },
      {
        headerName: "Visitors",
        headerClass: "header-custom",
        field: "VISITORS",
        flex: 0.35,
        cellStyle: {
          justifyContent: "flex-start",
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return noDecimalNumberFormatter(
            params.value as number,
            false,
            storeCurrencyConfig!.value!
          );
        },
        cellRenderer: (params) => {
          return (
            <IgTypography
              sx={{
                color: theme.palette.secondary.main,
                fontSize: "13px",
                fontStyle: params.value === "All Groups" ? "italic" : "none",
              }}
            >
              {params.valueFormatted}
            </IgTypography>
          );
        },
      },
      {
        headerName: "Orders",
        headerClass: "header-custom",
        flex: 0.35,
        field: "ORDERS",
        cellStyle: {
          justifyContent: "flex-start",
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return noDecimalNumberFormatter(
            params.value as number,
            false,
            storeCurrencyConfig!.value!
          );
        },
        cellRenderer: (params) => {
          return (
            <IgTypography
              sx={{
                color: theme.palette.secondary.main,
                fontSize: "13px",
                // fontStyle: params.value === "All Groups" ? "italic" : "none",
              }}
            >
              {params.valueFormatted}
            </IgTypography>
          );
        },
      },
      {
        headerName: "Revenue",
        headerClass: "header-custom",
        flex: 0.4,
        field: "TOTAL_REVENUE",
        cellStyle: {
          justifyContent: "flex-start",
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return currencyFormatter(
            params.value as number,
            false,
            storeCurrencyConfig!.value!
          );
        },
        cellRenderer: (params) => {
          return (
            <IgTypography
              sx={{
                color: theme.palette.secondary.main,
                fontSize: "13px",
                fontStyle: params.value === "All Groups" ? "italic" : "none",
              }}
            >
              {params.valueFormatted}
            </IgTypography>
          );
        },
      },
      {
        headerName: "CVR",
        headerClass: "header-custom",
        flex: 0.4,
        field: "CVR",
        cellStyle: {
          justifyContent: "flex-start",
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return percentTwoDecimalFormatter(
            params.value as number,
            false,
            storeCurrencyConfig!.value!
          );
        },
        cellRenderer: (params) => {
          const isHighestValue =
            getIdOfLargestColumnValue(
              "CVR",
              currValue,
              params?.data[currValue]
            ) === params?.data?.id;
          return (
            <IgTypography
              sx={{
                color: isHighestValue
                  ? theme.palette.success.main
                  : theme.palette.secondary.main,
                fontSize: "13px",
                fontStyle: params.value === "All Groups" ? "italic" : "none",
              }}
            >
              {params.valueFormatted}
            </IgTypography>
          );
        },
      },
      {
        headerName: "Revenue per Visitor",
        headerClass: "header-custom",
        flex: 0.4,
        field: "REV_PER_VISITOR",
        cellStyle: {
          justifyContent: "flex-start",
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return currencyFormatter(
            params.value as number,
            false,
            storeCurrencyConfig!.value!
          );
        },
        cellRenderer: (params) => {
          const isHighestValue =
            getIdOfLargestColumnValue(
              "REV_PER_VISITOR",
              currValue,
              params?.data[currValue]
            ) === params?.data?.id;
          return (
            <IgTypography
              sx={{
                color: isHighestValue
                  ? theme.palette.success.main
                  : theme.palette.secondary.main,
                fontSize: "13px",
                fontStyle: params.value === "All Groups" ? "italic" : "none",
              }}
            >
              {params.valueFormatted}
            </IgTypography>
          );
        },
      },
    ];
    if (shouldShowProfit?.value) {
      baseColumns.push(profit);
    }
    if (tabValue === 0) {
      return baseColumns.slice(2);
    } else return baseColumns;
  }, [
    data,
    compareChannelData,
    compareVisitorData,
    compareDeviceData,
    tabValue,
  ]);

  if (experiencesList.isSuccess && rows) {
    return (
      <StyledGrid
        sx={{ marginTop: "25px" }}
        container
        direction={"column"}
        item
        xs={12}
        md={12}
        rowSpacing={2}
      >
        <TableHeader tabValue={tabValue} setTabValue={setTabValue} />
        <Grid item>
          <IgTableWrapper height={"auto"}>
            <AgGridReact
              ref={gridRef}
              defaultColDef={defaultColDef}
              groupDefaultExpanded={collapse}
              rowData={rows}
              columnDefs={columnDefs}
              groupDisplayType={"groupRows"}
              gridOptions={gridOptions}
              suppressDragLeaveHidesColumns={true}
              suppressExcelExport={true}
              suppressCsvExport={true}
              suppressContextMenu={true}
              overlayLoadingTemplate={"<div>Loading Data ... </div>"}
              loadingCellRendererParams={loadingCellRendererParams}
              domLayout={"autoHeight"}
              autoGroupColumnDef={autoGroupColumnDef}
              groupRowRendererParams={groupRowRendererParams}
            />
          </IgTableWrapper>
        </Grid>
      </StyledGrid>
    );
  }

  return null;
};
